/**
 * PlaylistManager - Manages episode ordering and navigation
 * Handles next/previous episode logic and playlist operations
 */
export class PlaylistManager {
  constructor(episodes = []) {
    this.episodes = episodes;
    this.currentIndex = -1;
  }

  /**
   * Set episodes list
   * @param {Array} episodes - Array of episode objects
   */
  setEpisodes(episodes) {
    this.episodes = episodes;
  }

  /**
   * Set current episode by URL
   * @param {string} url - Episode URL to find and set as current
   * @returns {boolean} True if episode was found and set
   */
  setCurrentByUrl(url) {
    const index = this.episodes.findIndex(ep => ep.url === url);
    if (index !== -1) {
      this.currentIndex = index;
      return true;
    }
    return false;
  }

  /**
   * Set current episode by index
   * @param {number} index - Episode index
   * @returns {boolean} True if index is valid
   */
  setCurrentByIndex(index) {
    if (index >= 0 && index < this.episodes.length) {
      this.currentIndex = index;
      return true;
    }
    return false;
  }

  /**
   * Get current episode
   * @returns {Object|null} Current episode object or null
   */
  getCurrentEpisode() {
    if (this.currentIndex >= 0 && this.currentIndex < this.episodes.length) {
      return this.episodes[this.currentIndex];
    }
    return null;
  }

  /**
   * Get current episode index
   * @returns {number} Current index
   */
  getCurrentIndex() {
    return this.currentIndex;
  }

  /**
   * Get next episode
   * @returns {Object|null} Next episode or null if at end
   */
  getNext() {
    if (this.hasNext()) {
      return this.episodes[this.currentIndex + 1];
    }
    return null;
  }

  /**
   * Get previous episode
   * @returns {Object|null} Previous episode or null if at beginning
   */
  getPrevious() {
    if (this.hasPrevious()) {
      return this.episodes[this.currentIndex - 1];
    }
    return null;
  }

  /**
   * Move to next episode
   * @returns {Object|null} Next episode or null if at end
   */
  moveNext() {
    if (this.hasNext()) {
      this.currentIndex++;
      return this.getCurrentEpisode();
    }
    return null;
  }

  /**
   * Move to previous episode
   * @returns {Object|null} Previous episode or null if at beginning
   */
  movePrevious() {
    if (this.hasPrevious()) {
      this.currentIndex--;
      return this.getCurrentEpisode();
    }
    return null;
  }

  /**
   * Check if there is a next episode
   * @returns {boolean}
   */
  hasNext() {
    return this.currentIndex >= 0 && this.currentIndex < this.episodes.length - 1;
  }

  /**
   * Check if there is a previous episode
   * @returns {boolean}
   */
  hasPrevious() {
    return this.currentIndex > 0;
  }

  /**
   * Get total number of episodes
   * @returns {number}
   */
  getTotal() {
    return this.episodes.length;
  }

  /**
   * Get episode by URL
   * @param {string} url - Episode URL
   * @returns {Object|null} Episode object or null
   */
  getByUrl(url) {
    return this.episodes.find(ep => ep.url === url) || null;
  }

  /**
   * Get episode by index
   * @param {number} index - Episode index
   * @returns {Object|null} Episode object or null
   */
  getByIndex(index) {
    if (index >= 0 && index < this.episodes.length) {
      return this.episodes[index];
    }
    return null;
  }

  /**
   * Get all episodes
   * @returns {Array} All episodes
   */
  getAllEpisodes() {
    return this.episodes;
  }
}
